import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, HashRouter } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { ProductGrid } from './components/ProductGrid';
import { LearnGrow } from './components/LearnGrow';
import { Footer } from './components/Footer';
import { SellWithUsPage } from './pages/SellWithUsPage';
import { SellerDashboard } from './pages/SellerDashboard';
import { BuyerDashboard } from './pages/BuyerDashboard';
import { SellProductPage } from './pages/SellProductPage';
import { AuthProvider } from './components/auth/AuthProvider';
import { ThemeProvider } from './components/ThemeProvider';
import { CategoryGrid } from './components/CategoryGrid';
import { BrowseProductsPage } from './pages/BrowseProductsPage';
import { SubmitProductPage } from './pages/SubmitProductPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { CheckoutConfirmationPage } from './pages/CheckoutConfirmationPage';
import { PricingStrategiesPage } from './pages/learning/PricingStrategiesPage';
import { ProductDescriptionsPage } from './pages/learning/ProductDescriptionsPage';
import { ProductBundlesPage } from './pages/learning/ProductBundlesPage';
import { MarketingStrategyPage } from './pages/learning/MarketingStrategyPage';
import { BuildingAudiencePage } from './pages/learning/BuildingAudiencePage';
import { DesigningThumbnailsPage } from './pages/learning/DesigningThumbnailsPage';
import { ProfessionalLookPage } from './pages/learning/ProfessionalLookPage';
import { UploadFirstProductPage } from './pages/learning/UploadFirstProductPage';
import { PreparingFilesPage } from './pages/learning/PreparingFilesPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsPage } from './pages/TermsPage';
import { ProductCategoryPage } from './pages/ProductCategoryPage';
import { SubscribePage } from './pages/SubscribePage';
import { SellerProfilePage } from './pages/SellerProfilePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ScrollToTop } from './components/ScrollToTop';
import { CartProvider } from './components/cart/CartContext';
import { CartSidebar } from './components/cart/CartSidebar';
import { SettingsPage } from './pages/SettingsPage';
import { EmailSupport } from './pages/support/EmailSupport';
import { LiveChatSupport } from './pages/support/LiveChatSupport';
import { AccountSettingsLayout } from './pages/account-settings/AccountSettingsLayout';
import { ProfileSection } from './pages/account-settings/ProfileSection';
import { StoreSettingsSection } from './pages/account-settings/StoreSettingsSection';
import { PaymentsPayoutsSection } from './pages/account-settings/PaymentsPayoutsSection';
import { ProductPreferencesSection } from './pages/account-settings/ProductPreferencesSection';
import { NotificationPreferencesSection } from './pages/account-settings/NotificationPreferencesSection';
import { SecuritySection } from './pages/account-settings/SecuritySection';
import { SupportFeedbackSection } from './pages/account-settings/SupportFeedbackSection';
import { AccountActionsSection } from './pages/account-settings/AccountActionsSection';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import SecurityMonitoringPage from './pages/security/SecurityMonitoringPage';
import SecurityPostureDashboard from './pages/security/SecurityPostureDashboard';
import PredictiveThreatDashboard from './pages/security/PredictiveThreatDashboard';
// Detect if we should use hash routing
// This helps with static hosting platforms that don't support proper URL rewriting
const useHashRouter = window.location.href.includes('use-hash-router=true') || process.env.NODE_ENV === 'production';
// HomePage defined outside App to prevent unmount/remount on re-renders
const HomePage: React.FC<{
  scrolled: boolean;
}> = ({
  scrolled
}) => <div className="w-full bg-white dark:bg-[#0F1F1D] overflow-x-hidden transition-colors duration-300" data-id="element-0">
    <Navbar scrolled={scrolled} data-id="element-1" />
    <main data-id="element-2">
      <Hero data-id="element-3" />
      <CategoryGrid data-id="element-4" />
      <LearnGrow data-id="element-5" />
    </main>
    <Footer data-id="element-6" />
  </div>;
export function App() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  // Define routes to be used in both router types
  const appRoutes = <Routes data-id="element-7">
      <Route path="/" element={<HomePage scrolled={scrolled} data-id="element-9" />} data-id="element-8" />
      <Route path="/sell-with-us" element={<SellWithUsPage data-id="element-11" />} data-id="element-10" />
      {/* Protected Seller Routes */}
      <Route path="/seller-dashboard" element={<ProtectedRoute requiredRoles={['seller']} showAccessDenied data-id="element-13">
            <SellerDashboard data-id="element-14" />
          </ProtectedRoute>} data-id="element-12" />
      <Route path="/buyer-dashboard" element={<ProtectedRoute requireAuth data-id="element-16">
            <BuyerDashboard data-id="element-17" />
          </ProtectedRoute>} data-id="element-15" />
      <Route path="/sell-product" element={<ProtectedRoute requiredRoles={['seller']} data-id="element-19">
            <SellProductPage data-id="element-20" />
          </ProtectedRoute>} data-id="element-18" />
      <Route path="/submit" element={<ProtectedRoute requiredRoles={['seller']} data-id="element-22">
            <SubmitProductPage data-id="element-23" />
          </ProtectedRoute>} data-id="element-21" />
      {/* Public Product Routes */}
      <Route path="/browse" element={<BrowseProductsPage data-id="element-25" />} data-id="element-24" />
      <Route path="/product/:productId" element={<ProductDetailPage data-id="element-27" />} data-id="element-26" />
      {/* Checkout route */}
      <Route path="/checkout" element={<ProtectedRoute requireAuth data-id="element-29">
            <CheckoutPage data-id="element-30" />
          </ProtectedRoute>} data-id="element-28" />
      {/* Protected Settings Routes */}
      <Route path="/settings" element={<ProtectedRoute requireAuth data-id="element-32">
            <SettingsPage data-id="element-33" />
          </ProtectedRoute>} data-id="element-31" />
      {/* Account Settings pages - all require authentication */}
      <Route path="/account-settings" element={<ProtectedRoute requireAuth data-id="element-35">
            <AccountSettingsLayout data-id="element-36" />
          </ProtectedRoute>} data-id="element-34">
        <Route path="profile" element={<ProfileSection data-id="element-38" />} data-id="element-37" />
        <Route path="store" element={<ProtectedRoute requiredRoles={['seller']} data-id="element-40">
              <StoreSettingsSection data-id="element-41" />
            </ProtectedRoute>} data-id="element-39" />
        <Route path="payments" element={<ProtectedRoute requiredRoles={['seller']} data-id="element-43">
              <PaymentsPayoutsSection data-id="element-44" />
            </ProtectedRoute>} data-id="element-42" />
        <Route path="product" element={<ProductPreferencesSection data-id="element-46" />} data-id="element-45" />
        <Route path="notifications" element={<NotificationPreferencesSection data-id="element-48" />} data-id="element-47" />
        <Route path="security" element={<SecuritySection data-id="element-50" />} data-id="element-49" />
        <Route path="support" element={<SupportFeedbackSection data-id="element-52" />} data-id="element-51" />
        <Route path="actions" element={<AccountActionsSection data-id="element-54" />} data-id="element-53" />
      </Route>

      {/* Security Monitoring - Admin only */}
      <Route path="/security-monitoring" element={<ProtectedRoute requiredRoles={['admin']} showAccessDenied data-id="element-56">
            <SecurityMonitoringPage data-id="element-57" />
          </ProtectedRoute>} data-id="element-55" />

      {/* Security Posture Dashboard - Admin only */}
      <Route path="/security-posture" element={<ProtectedRoute requiredRoles={['admin']} showAccessDenied data-id="element-59">
            <SecurityPostureDashboard data-id="element-60" />
          </ProtectedRoute>} data-id="element-58" />

      {/* Predictive Threat Dashboard - Admin only */}
      <Route path="/predictive-threats" element={<ProtectedRoute requiredRoles={['admin']} showAccessDenied data-id="element-62">
            <PredictiveThreatDashboard data-id="element-63" />
          </ProtectedRoute>} data-id="element-61" />

      {/* Learning pages */}
      <Route path="/learn/pricing-strategies" element={<PricingStrategiesPage data-id="element-65" />} data-id="element-64" />
      <Route path="/learn/product-descriptions" element={<ProductDescriptionsPage data-id="element-67" />} data-id="element-66" />
      <Route path="/learn/product-bundles" element={<ProductBundlesPage data-id="element-69" />} data-id="element-68" />
      <Route path="/learn/marketing-strategy" element={<MarketingStrategyPage data-id="element-71" />} data-id="element-70" />
      <Route path="/learn/building-audience" element={<BuildingAudiencePage data-id="element-73" />} data-id="element-72" />
      <Route path="/learn/designing-thumbnails" element={<DesigningThumbnailsPage data-id="element-75" />} data-id="element-74" />
      <Route path="/learn/professional-look" element={<ProfessionalLookPage data-id="element-77" />} data-id="element-76" />
      <Route path="/learn/upload-first-product" element={<UploadFirstProductPage data-id="element-79" />} data-id="element-78" />
      <Route path="/learn/preparing-files" element={<PreparingFilesPage data-id="element-81" />} data-id="element-80" />
      <Route path="/reset-password" element={<ResetPasswordPage data-id="element-83" />} data-id="element-82" />
      {/* Footer pages */}

      <Route path="/about" element={<AboutPage data-id="element-85" />} data-id="element-84" />

      <Route path="/contact" element={<ContactPage data-id="element-87" />} data-id="element-86" />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-89" />} data-id="element-88" />
      <Route path="/terms" element={<TermsPage data-id="element-91" />} data-id="element-90" />

      {/* Support pages */}
      <Route path="/support/email" element={<EmailSupport data-id="element-93" />} data-id="element-92" />
      <Route path="/support/chat" element={<LiveChatSupport data-id="element-95" />} data-id="element-94" />
      {/* Product category pages */}
      <Route path="/products/:category" element={<ProductCategoryPage data-id="element-97" />} data-id="element-96" />

      {/* Subscription pages */}
      <Route path="/subscribe/:planId" element={<ProtectedRoute requireAuth data-id="element-99">
            <SubscribePage data-id="element-100" />
          </ProtectedRoute>} data-id="element-98" />
      {/* Seller profile page */}
      <Route path="/seller/:sellerId" element={<SellerProfilePage data-id="element-102" />} data-id="element-101" />
      {/* 404 catch-all */}
      <Route path="*" element={<NotFoundPage data-id="element-104" />} data-id="element-103" />
    </Routes>;
  return <ThemeProvider data-id="element-105">
      <AuthProvider data-id="element-106">
        <CartProvider data-id="element-107">
          {useHashRouter ?
        // Use HashRouter for production or when explicitly requested
        <HashRouter data-id="element-108">
              <ScrollToTop data-id="element-109" />
              {appRoutes}
              <CartSidebar data-id="element-110" />
            </HashRouter> :
        // Use BrowserRouter for development
        <BrowserRouter data-id="element-111">
              <ScrollToTop data-id="element-112" />
              {appRoutes}
              <CartSidebar data-id="element-113" />
            </BrowserRouter>}
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>;
}